import { useState, useRef, useEffect } from 'react';
import { C, t } from '../../Dashboard/components/dashboardConstants';

const DEFAULT_BOARD_EMOJI = '💛';
const DEFAULT_BOARD_DESC = '나중에 지원할 공고 모음';
const DEFAULT_BOARD_COLOR = '#FFF9E6';

function BoardCard({ board, onClick, onEdit, onDelete, onSetDefault }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const emoji = board.emoji || (board.isDefault ? DEFAULT_BOARD_EMOJI : '📁');
  const emojiBg = board.color || (board.isDefault ? DEFAULT_BOARD_COLOR : C.bg);
  const desc = board.desc || board.description || (board.isDefault ? DEFAULT_BOARD_DESC : '');

  const menuItems = [
    {
      label: '보기',
      handler: () => { setMenuOpen(false); onClick(board); },
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <ellipse cx="12" cy="12" rx="9" ry="6" stroke={C.bk} strokeWidth="1.5"/>
          <circle cx="12" cy="12" r="2.5" stroke={C.bk} strokeWidth="1.5"/>
        </svg>
      ),
    },
    {
      label: '수정',
      handler: () => { setMenuOpen(false); onEdit(board); },
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke={C.bk} strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke={C.bk} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    ...(!board.isDefault ? [{
      label: '기본 보관함 설정',
      handler: () => { setMenuOpen(false); onSetDefault(board); },
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M12 17.27L18.18 21 16.54 13.97 22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27z" stroke={C.bk} strokeWidth="1.5" strokeLinejoin="round"/>
        </svg>
      ),
    }] : []),
    ...(!board.isDefault ? [{
      label: '삭제',
      handler: () => { setMenuOpen(false); onDelete(board); },
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      color: '#EF4444',
    }] : []),
  ];

  return (
    <div
      onClick={() => onClick(board)}
      style={{
        background: C.white,
        border: `1px solid ${C.g200}`,
        borderRadius: '16px',
        padding: '20px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '16px',
        cursor: 'pointer',
        position: 'relative',
      }}
    >
      {/* 이모지 아이콘 + 뱃지 */}
      <div style={{ position: 'relative', width: 48, height: 48, flexShrink: 0 }}>
        <div style={{
          width: 48, height: 48, borderRadius: '12px',
          background: emojiBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '22px',
        }}>
          {emoji}
        </div>
        {board.count != null && (
          <div style={{
            position: 'absolute', top: -6, left: -6,
            width: 20, height: 20, borderRadius: '50%',
            background: C.bk,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ ...t('10px', 700, C.white) }}>{board.count}</span>
          </div>
        )}
      </div>

      {/* 텍스트 */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
          <span style={{ ...t('15px', 600, C.bk), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {board.name}
          </span>
          {board.isDefault && (
            <span style={{
              padding: '1px 7px', borderRadius: '20px', flexShrink: 0,
              background: C.bg, border: `1px solid ${C.g200}`,
              ...t('11px', 400, C.sub),
            }}>기본</span>
          )}
        </div>
        {desc && (
          <span style={{
            ...t('12px', 400, C.sub),
            overflow: 'hidden', textOverflow: 'ellipsis',
            display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical',
          }}>
            {desc}
          </span>
        )}
      </div>

      {/* "..." 메뉴 */}
      <div
        ref={menuRef}
        style={{ flexShrink: 0 }}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={(e) => { e.stopPropagation(); setMenuOpen(p => !p); }}
          style={{
            width: 32, height: 32, borderRadius: '8px',
            background: 'transparent', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px', padding: 0,
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = C.g200}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          {[0, 1, 2].map(i => (
            <div key={i} style={{ width: 3, height: 3, borderRadius: '50%', background: C.sub }} />
          ))}
        </button>

        {menuOpen && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 4px)', right: 16,
            background: C.white, border: `1px solid ${C.g200}`,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)', borderRadius: '14px',
            padding: '8px 18px', zIndex: 20, minWidth: '120px',
          }}>
            {menuItems.map(({ label, handler, icon, color }) => (
              <button key={label} onClick={handler} style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '10px 0', width: '100%',
                background: 'none', border: 'none', cursor: 'pointer',
                whiteSpace: 'nowrap',
                ...t('14px', 500, color || C.bk),
              }}>
                {icon}
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ArchiveBoardView({ boards, onBoardClick, onEditBoard, onDeleteBoard, onSetDefaultBoard }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '16px',
      paddingTop: '32px',
    }}>
      {boards.map(b => (
        <BoardCard key={b.id} board={b} onClick={onBoardClick} onEdit={onEditBoard} onDelete={onDeleteBoard} onSetDefault={onSetDefaultBoard} />
      ))}
    </div>
  );
}
